/* ============================================================================
   Merchant Setup → Zone Mapping → Zone Override   (route /merchant-config/zones/override)

   Per-merchant lane overrides on top of the merchant base zone map. Same API
   as the legacy Courier Setup › Zone Override page:
     POST config/merchant/{merchantId}/zones   body: [{f, t, z, mi, mx, dl}]
   The upload is a DELTA: each row upserts the (f,t) lane; dl=1 deletes it;
   lanes not in the payload are untouched. Writes go to MySQL
   (merchant_zone_overrides). Bulk = xlsx (Sheet1) parsed in the browser and
   posted to the same endpoint; sample file BULK_ZONE_OVERRIDE.
   Current overrides table is read from the same MySQL table, live rows only, via
   GET tools/zoneoverride/page?merchantOid=&size=&lastF=&lastT= (keyset pages),
   and reloads after every save / delete / bulk upload. Deleting is done from
   that table (row menu or bulk select → confirm → {f, t, dl:1} rows); the
   editor itself only adds or replaces lanes.
   Visual language: supply-side module (SupplyHero, flat cards, FileDrop, Modal).
   ========================================================================== */
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Box, Button, IconButton, Stack, TextField, Tooltip, Typography} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'
import AltRouteOutlinedIcon from '@mui/icons-material/AltRouteOutlined'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import RefreshIcon from '@mui/icons-material/Refresh'
import * as XLSX from 'xlsx/xlsx.mjs'
import {apiConstants, get, post} from '../../common/constants'
import PageTitle from '../../components/PageTitle'
import DownloadSampleFile from '../../components/DownloadSampleFile'
import {Badge, ConfirmDialog, FileDrop, Modal, SearchSelect, SupplyHero, SupplyTable, useToast} from '../supply-side/components'
import {MERCHANT_ZONES, fmtTat, isPincode, merchantZoneName, zoneTone} from './zoneMeta'
import {
  BTN_SX, CARD, CONTROL_H, Eyebrow, FIELD_SX, LABEL, MONO, NO_P_MARGIN, Note, PAGE_MAX, PIN_INPUT, SELECT_SX, SectionLabel, ZoneCard,
  apiMessage, digitsOnly, useMerchantOptions,
} from './zoneUi'

/** Digits and at most one dot ("1..2" can't be typed), for the TAT inputs. */
const decimalOnly = (v) => String(v || '').replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1')

const BLANK_ROW = () => ({id: Math.random().toString(36).slice(2), f: '', t: '', z: '', mi: '', mx: ''})

/** Zone picker options for an editor row: "Local (1)" … "Special (5)". */
const ZONE_OPTIONS = MERCHANT_ZONES.map((z) => ({value: z.value, label: `${z.label} (${z.value})`}))

/** Editor rows → the delta payload the API expects (numbers, blanks dropped). Deletes go
 * through the Current overrides table instead (dl=1 rows, see deleteLanes). */
const toDeltaRows = (list) =>
  (list || [])
    .filter((r) => r && (String(r.f || '').trim() || String(r.t || '').trim()))
    .map((r) => {
      const row = {f: Number(String(r.f).trim()), t: Number(String(r.t).trim())}
      if (r.z !== '' && r.z != null) row.z = Number(r.z)
      if (r.mi !== '' && r.mi != null) row.mi = Number(r.mi)
      if (r.mx !== '' && r.mx != null) row.mx = Number(r.mx)
      return row
    })

/** One short line from the delta API's counts, in place of its raw JSON. */
const resultMessage = (d) => {
  const n = (v) => Number(v) || 0
  if (!d || typeof d !== 'object') return {text: 'Done.', tone: 'success'}
  const parts = []
  if (n(d.appliedUpserts)) parts.push(`${n(d.appliedUpserts)} lane${n(d.appliedUpserts) === 1 ? '' : 's'} saved`)
  if (n(d.deletesMatched)) parts.push(`${n(d.deletesMatched)} removed`)
  if (n(d.skippedRows)) parts.push(`${n(d.skippedRows)} row${n(d.skippedRows) === 1 ? '' : 's'} skipped`)
  if (!parts.length) return {text: 'No changes were made.', tone: 'warning'}
  return {text: parts.join(', ') + '.', tone: n(d.skippedRows) ? 'warning' : 'success'}
}

/* ── current overrides ──────────────────────────────────────────────────── */
const CURRENT_PAGE_SIZE = 5000
const CURRENT_MAX_ROWS = 50000

/* Override vs the lane's base ("Prozo") zone. Zones run 1 Local (cheapest) … 5 Special (dearest),
   so a higher override code prices the lane dearer for the merchant. */
const COMPARE = {
  up: {label: 'Dearer', mark: '▲', fg: '#146C43', bg: '#E3F3EA', order: 3},
  down: {label: 'Cheaper', mark: '▼', fg: '#B42318', bg: '#FDEBE9', order: 1},
  same: {label: 'Same', mark: '=', fg: '#39414D', bg: '#EEF0F3', order: 2},
  na: {label: '—', mark: '', fg: '#98A0AD', bg: 'transparent', order: 0},
}

/* Table look per the Merchant Zone Override mockup: sentence-case headers on a light band,
   compact rows, hairline dividers, no zebra. `&&` doubles the class so these beat the
   scoped `.supply-scope table.tbl …` rules. */
const TABLE_SX = {
  '&& table.tbl th': {textTransform: 'none', letterSpacing: 0, fontSize: 12.5, fontWeight: 600, color: '#39414D',
    background: '#F8F9FB', padding: '10px 12px', borderBottom: '1px solid #E2E5EA'},
  '&& table.tbl td': {padding: '9px 12px', borderBottom: '1px solid #EEF0F3', verticalAlign: 'middle', color: '#1E2329'},
  '&& table.tbl tbody tr:nth-of-type(even)': {background: 'transparent'},
  '&& table.tbl tbody tr:hover': {background: '#FBFCFE'},
  '&& table.tbl tbody tr:last-child td': {borderBottom: 0},
}
const compareOf = (z, base) => (z == null || base == null ? 'na' : z > base ? 'up' : z < base ? 'down' : 'same')

const fmtDate = (d) => (d ? d.toLocaleDateString('en-IN', {day: '2-digit', month: 'short', year: 'numeric'}) : '')
const fmtDateTime = (d) => (d ? d.toLocaleString('en-IN', {day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'}) : '')

/** One API row (GET tools/zoneoverride/page?enrich=true) → table row. */
const toCurrentRow = (r) => {
  const base = r.baseZone == null ? null : Number(r.baseZone)
  const z = r.z == null ? null : Number(r.z)
  const compare = compareOf(z, base)
  const updated = r.updatedAt ? new Date(r.updatedAt) : null
  return {
    id: r.f + '-' + r.t,
    f: String(r.f),
    t: String(r.t),
    fromCity: r.fromCity || '',
    toCity: r.toCity || '',
    z,
    zoneName: z == null ? 'TAT only' : merchantZoneName(z),
    baseZone: base,
    baseName: base == null ? 'Not mapped' : merchantZoneName(base),
    compare,
    compareLabel: COMPARE[compare].label,
    mi: r.mi,
    mx: r.mx,
    tat: fmtTat(r.mi, r.mx),
    updated: updated && !isNaN(updated.getTime()) ? updated : null,
  }
}

/** Zone as a pill: code in a circle + name. `muted` = the base zone (outlined, so the override stands out). */
function ZonePill({code, muted}) {
  const tone = zoneTone(code)
  return (
    <Box component='span' sx={{display: 'inline-flex', alignItems: 'center', gap: 0.75, height: 26, pl: '4px', pr: 1.25, borderRadius: 999,
      fontSize: 12.5, fontWeight: 600, lineHeight: 1, whiteSpace: 'nowrap', color: tone.fg, boxSizing: 'border-box',
      bgcolor: muted ? '#fff' : tone.bg, border: '1px solid', borderColor: muted ? '#E2E5EA' : 'transparent'}}>
      {/* explicit line-height = box height keeps the digit centred (the scoped font's ascent pushes it low otherwise) */}
      <Box component='span' sx={{width: 18, height: 18, lineHeight: '18px', borderRadius: '50%', textAlign: 'center',
        fontSize: 11, fontWeight: 700, color: '#fff', bgcolor: tone.fg, flexShrink: 0}}>{code}</Box>
      {merchantZoneName(code)}
    </Box>
  )
}
ZonePill.propTypes = {code: PropTypes.number, muted: PropTypes.bool}

function PinCell({pin, city}) {
  return (
    <Box sx={{lineHeight: 1.35}}>
      <Box sx={{fontFamily: MONO, fontSize: 13.5, fontWeight: 600, color: '#1E2329'}}>{pin}</Box>
      {city ? <Box sx={{fontSize: 12, color: '#677080', maxWidth: 150, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{city}</Box> : null}
    </Box>
  )
}
PinCell.propTypes = {pin: PropTypes.string, city: PropTypes.string}

const NUM = {fontSize: 13.5, fontWeight: 600, color: '#1E2329', fontVariantNumeric: 'tabular-nums'}
const SOFT = {fontSize: 12.5, color: '#677080'}

/** TAT as "1 – 2 days" (spaced range, muted unit); "3 days", "min 2 days", "max 5 days"; "—" when unset. */
function TatCell({mi, mx}) {
  const a = mi == null ? null : Number(mi)
  const b = mx == null ? null : Number(mx)
  if (a == null && b == null) return MUTED('—')
  // the one number shown when it is not a range (drives "day" vs "days")
  const single = a != null && b != null ? (a === b ? a : null) : a != null ? a : b
  let body
  if (a != null && b != null && a !== b) {
    body = (
      <>
        <Box component='span' sx={NUM}>{a}</Box>
        <Box component='span' sx={{...SOFT, mx: 0.5}}>to</Box>
        <Box component='span' sx={NUM}>{b}</Box>
      </>
    )
  } else if (a != null && b != null) {
    body = <Box component='span' sx={NUM}>{a}</Box>
  } else {
    body = (
      <>
        <Box component='span' sx={{...SOFT, mr: 0.5}}>{a != null ? 'min' : 'max'}</Box>
        <Box component='span' sx={NUM}>{a != null ? a : b}</Box>
      </>
    )
  }
  return (
    <Box component='span' sx={{whiteSpace: 'nowrap'}}>
      {body}
      <Box component='span' sx={{...SOFT, ml: 0.5}}>{single === 1 ? 'day' : 'days'}</Box>
    </Box>
  )
}
TatCell.propTypes = {mi: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), mx: PropTypes.oneOfType([PropTypes.number, PropTypes.string])}

const MUTED = (text) => <Box component='span' sx={{fontSize: 12.5, color: 'text.disabled'}}>{text}</Box>

/** Columns of the Current overrides table; Edit / Delete call back into the page. */
const currentColumns = ({onEdit, onDelete}) => [
  {key: 'f', label: 'From pincode', value: (r) => Number(r.f), exportValue: (r) => r.f, render: (r) => <PinCell pin={r.f} city={r.fromCity} />},
  {key: 't', label: 'To pincode', value: (r) => Number(r.t), exportValue: (r) => r.t, render: (r) => <PinCell pin={r.t} city={r.toCity} />},
  {
    key: 'z',
    label: 'Override zone',
    value: (r) => (r.z == null ? 0 : r.z),
    exportValue: (r) => r.zoneName,
    render: (r) => (r.z == null ? MUTED('TAT only') : <ZonePill code={r.z} />),
  },
  {
    key: 'baseZone',
    label: 'Prozo zone',
    value: (r) => (r.baseZone == null ? 0 : r.baseZone),
    exportValue: (r) => r.baseName,
    render: (r) => (r.baseZone == null ? MUTED('Not mapped') : <ZonePill code={r.baseZone} muted />),
  },
  {
    key: 'compare',
    label: 'vs Prozo',
    value: (r) => COMPARE[r.compare].order,
    exportValue: (r) => r.compareLabel,
    render: (r) => {
      const c = COMPARE[r.compare]
      if (r.compare === 'na') {
        return (
          <Tooltip title={r.z == null ? 'TAT-only override: the zone is not changed' : 'This lane has no base zone to compare with'}>
            <span>{MUTED('—')}</span>
          </Tooltip>
        )
      }
      return (
        <Box component='span' sx={{display: 'inline-flex', alignItems: 'center', gap: 0.5, height: 22, px: 1, borderRadius: '6px',
          fontSize: 12, fontWeight: 600, lineHeight: 1, whiteSpace: 'nowrap', color: c.fg, bgcolor: c.bg}}>
          {c.mark} {c.label}
        </Box>
      )
    },
  },
  {
    key: 'tat',
    label: 'TAT',
    value: (r) => (r.mx != null ? Number(r.mx) : r.mi != null ? Number(r.mi) : -1),
    exportValue: (r) => (r.tat ? `${r.tat} days` : ''),
    render: (r) => <TatCell mi={r.mi} mx={r.mx} />,
  },
  {
    key: 'updated',
    label: 'Updated',
    value: (r) => (r.updated ? r.updated.getTime() : 0),
    exportValue: (r) => fmtDateTime(r.updated),
    render: (r) =>
      r.updated ? (
        <Tooltip title={fmtDateTime(r.updated)}>
          <Typography component='span' sx={{fontSize: 13, whiteSpace: 'nowrap'}}>{fmtDate(r.updated)}</Typography>
        </Tooltip>
      ) : MUTED('—'),
  },
  {
    key: 'actions',
    label: '',
    sortable: false,
    export: false,
    align: 'right',
    render: (r) => (
      <Stack direction='row' spacing={0.5} justifyContent='flex-end'>
        <Box component='button' type='button' onClick={(e) => { e.stopPropagation(); onEdit(r) }} sx={ACTION_BTN}>Edit</Box>
        <Box component='button' type='button' onClick={(e) => { e.stopPropagation(); onDelete(r) }} sx={ACTION_BTN_DANGER}>Remove</Box>
      </Stack>
    ),
  },
]

/* mockup `.btn.ghost.sm` / `.btn.danger-o.sm` */
const ACTION_BTN = {height: 28, px: '10px', borderRadius: '6px', border: '1px solid #E2E5EA', bgcolor: '#fff', color: '#39414D',
  fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600, lineHeight: 1, cursor: 'pointer', whiteSpace: 'nowrap',
  '&:hover': {bgcolor: '#EEF0F3'}}
const ACTION_BTN_DANGER = {...ACTION_BTN, color: '#B42318', borderColor: '#E7B3AE', '&:hover': {bgcolor: '#FDEBE9'}}

const CURRENT_FILTERS = [
  {key: 'compareLabel', label: 'vs Prozo', type: 'multi', options: ['Dearer', 'Cheaper', 'Same', '—'].map((v) => ({value: v, label: v === '—' ? 'Not comparable' : v}))},
  {key: 'zoneName', label: 'Override zone', type: 'multi',
    options: MERCHANT_ZONES.map((z) => ({value: z.label, label: z.label})).concat([{value: 'TAT only', label: 'TAT only'}])},
]

const validateRows = (rows) => {
  if (!rows.length) return 'Add at least one lane.'
  for (const r of rows) {
    if (!isPincode(r.f) || !isPincode(r.t)) return 'Every lane needs a 6-digit from and to pincode.'
    if (r.z == null && r.mi == null && r.mx == null) return `Lane ${r.f} → ${r.t}: set a zone and/or TAT.`
    if ((r.mi != null && !Number.isFinite(r.mi)) || (r.mx != null && !Number.isFinite(r.mx))) {
      return `Lane ${r.f} → ${r.t}: TAT must be a number of days.`
    }
    if (r.mi != null && r.mx != null && r.mi > r.mx) return `Lane ${r.f} → ${r.t}: min TAT is more than max TAT.`
  }
  return null
}

export default function MerchantZoneOverride() {
  const toast = useToast()
  const [merchant, setMerchant] = useState('')
  const [rows, setRows] = useState([BLANK_ROW()])
  const [saving, setSaving] = useState(false)
  const [removing, setRemoving] = useState(false)
  const [bulkOpen, setBulkOpen] = useState(false)
  // the merchant on screen right now — a save/remove that finishes after a switch must not reload
  // (and so relabel) the table for the merchant it started with
  const merchantRef = useRef(merchant)
  merchantRef.current = merchant

  const {options: merchantOptions, loading: merchantsLoading, labelOf} = useMerchantOptions()
  const merchantLabel = labelOf(merchant)

  // current overrides of the selected merchant; a sequence number drops stale responses
  // when the merchant changes mid-load
  const [current, setCurrent] = useState(null) // {rows, capped} | null
  const [currentLoading, setCurrentLoading] = useState(false)
  const [currentError, setCurrentError] = useState(null)
  const loadSeq = useRef(0)

  const loadCurrent = useCallback(async (merchantOid) => {
    const seq = ++loadSeq.current
    setCurrentLoading(true)
    setCurrentError(null)
    try {
      const out = []
      let lastF = null
      let lastT = null
      for (;;) {
        const params = {merchantOid, size: CURRENT_PAGE_SIZE, enrich: true}
        if (lastF != null) {
          params.lastF = lastF
          params.lastT = lastT
        }
        const res = await get(apiConstants.TOOL_ZONE_OVERRIDE_PAGE, params)
        if (seq !== loadSeq.current) return
        const page = res?.data?.rows || []
        page.forEach((r) => out.push(toCurrentRow(r)))
        if (page.length < CURRENT_PAGE_SIZE || out.length >= CURRENT_MAX_ROWS) break
        lastF = res.data.nextLastF
        lastT = res.data.nextLastT
      }
      setCurrent({rows: out.slice(0, CURRENT_MAX_ROWS), capped: out.length >= CURRENT_MAX_ROWS})
    } catch (err) {
      if (seq === loadSeq.current) setCurrentError(apiMessage(err))
    } finally {
      if (seq === loadSeq.current) setCurrentLoading(false)
    }
  }, [])

  useEffect(() => {
    if (merchant) {
      setCurrent(null)
      loadCurrent(merchant)
    } else {
      loadSeq.current++
      setCurrent(null)
      setCurrentError(null)
      setCurrentLoading(false)
    }
  }, [merchant, loadCurrent])

  // delete existing overrides from the Current overrides table (row menu or bulk select):
  // same delta endpoint, one {f, t, dl:1} row per lane
  const [pendingDelete, setPendingDelete] = useState(null) // rows awaiting confirmation
  const clearSelection = useRef(null)

  const deleteLanes = async (lanes) => {
    setPendingDelete(null)
    const oid = merchant
    setRemoving(true)
    try {
      const res = await post(`${apiConstants.MERCHANT_CONFIG}/${oid}/zones`,
        lanes.map((r) => ({f: Number(r.f), t: Number(r.t), dl: 1})))
      const msg = resultMessage(res?.data)
      toast(msg.text, msg.tone)
      if (clearSelection.current) clearSelection.current()
      if (merchantRef.current === oid) loadCurrent(oid)
    } catch (err) {
      toast(apiMessage(err), 'error')
    } finally {
      setRemoving(false)
    }
  }

  // Switching merchant with unsaved editor rows would let them be saved to the new merchant — ask
  // first, then clear them. The very first pick keeps rows typed before a merchant was chosen.
  const [pendingMerchant, setPendingMerchant] = useState(null) // {value} awaiting confirmation
  const onMerchantChange = (v) => {
    if (v === merchant) return
    const hasRows = rows.some((r) => String(r.f).trim() || String(r.t).trim())
    if (merchant && hasRows) {
      setPendingMerchant({value: v})
      return
    }
    setMerchant(v)
  }
  const switchMerchant = (v) => {
    setPendingMerchant(null)
    setRows([BLANK_ROW()])
    setMerchant(v)
  }

  // Edit: copy an existing override into the editor (replacing an empty editor, else appended;
  // a lane already in the editor is not added twice), then bring the editor into view
  const editorRef = useRef(null)
  const editLane = useCallback((r) => {
    setRows((rs) => {
      if (rs.some((x) => x.f === r.f && x.t === r.t)) return rs
      const row = {...BLANK_ROW(), f: r.f, t: r.t, z: r.z == null ? '' : String(r.z),
        mi: r.mi == null ? '' : String(r.mi), mx: r.mx == null ? '' : String(r.mx)}
      const empty = rs.every((x) => !String(x.f).trim() && !String(x.t).trim())
      return empty ? [row] : [...rs, row]
    })
    if (editorRef.current) editorRef.current.scrollIntoView({behavior: 'smooth', block: 'start'})
    toast(`${r.f} → ${r.t} is in the editor. Change it and save.`, 'info')
  }, [toast])

  const columns = useMemo(
    () => currentColumns({
      onEdit: editLane,
      onDelete: (r) => {
        clearSelection.current = null
        setPendingDelete([r])
      },
    }),
    [editLane]
  )

  const save = async () => {
    if (!merchant) return toast('Select a merchant first.', 'warning')
    const delta = toDeltaRows(rows)
    const problem = validateRows(delta)
    if (problem) return toast(problem, 'warning')
    const oid = merchant
    const label = merchantLabel || 'the merchant'
    setSaving(true)
    try {
      const res = await post(`${apiConstants.MERCHANT_CONFIG}/${oid}/zones`, delta)
      const msg = resultMessage(res?.data)
      toast(`${label}: ${msg.text}`, msg.tone)
      if (merchantRef.current === oid) {
        setRows([BLANK_ROW()])
        loadCurrent(oid)
      }
    } catch (err) {
      toast(apiMessage(err), 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <PageTitle>Zone Override</PageTitle>
      <SupplyHero
        icon={<AltRouteOutlinedIcon />}
        eyebrow={<Eyebrow>Merchant Setup · Zone Mapping</Eyebrow>}
        title='Zone Override'
        subtitle='Give one merchant a different zone or TAT on specific lanes.'
        actions={
          <Button variant='outlined' size='small' startIcon={<UploadFileOutlinedIcon />} onClick={() => setBulkOpen(true)}
            sx={{textTransform: 'none', fontWeight: 600, borderRadius: '10px', color: '#fff', borderColor: 'rgba(255,255,255,.3)', '&:hover': {borderColor: '#fff', bgcolor: 'rgba(255,255,255,.08)'}}}>
            Bulk upload (xlsx)
          </Button>
        }
        sx={{maxWidth: PAGE_MAX}}
      />

      <ZoneCard title='Merchant'>
        <Box sx={{maxWidth: 460}}>
          <SectionLabel sx={{mb: 0.75}}>Merchant *</SectionLabel>
          <SearchSelect
            value={merchant}
            loading={merchantsLoading}
            placeholder={merchantsLoading ? 'Loading merchants…' : 'Search a merchant…'}
            options={merchantOptions}
            disabled={saving || removing}
            onChange={onMerchantChange}
          />
        </Box>
      </ZoneCard>

      <Box ref={editorRef} sx={{scrollMarginTop: 80}} />
      <OverrideEditor rows={rows} setRows={setRows} merchant={merchant} saving={saving} onSave={save} />

      <CurrentOverrides
        merchant={merchant}
        merchantLabel={merchantLabel}
        current={current}
        loading={currentLoading}
        error={currentError}
        columns={columns}
        onReload={() => loadCurrent(merchant)}
        onBulkRemove={(sel, clear) => {
          clearSelection.current = clear
          setPendingDelete(sel)
        }}
      />

      <ConfirmDialog
        open={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        danger
        okLabel='Remove'
        title={pendingDelete && pendingDelete.length > 1 ? `Remove ${pendingDelete.length} overrides?` : 'Remove this override?'}
        body='These lanes go back to the base zone and base TAT for this merchant.'
        bullets={(pendingDelete || []).slice(0, 5).map((r) => `${r.f} → ${r.t} · ${r.z == null ? 'TAT only' : `${r.zoneName} (${r.z})`}${r.tat ? ` · TAT ${r.tat}` : ''}`)
          .concat(pendingDelete && pendingDelete.length > 5 ? [`and ${pendingDelete.length - 5} more`] : [])}
        onOk={() => deleteLanes(pendingDelete || [])}
      />

      <ConfirmDialog
        open={!!pendingMerchant}
        onClose={() => setPendingMerchant(null)}
        okLabel='Discard and switch'
        title='Switch merchant?'
        body='The lanes in the editor are not saved. Switching clears them, so they cannot be saved to the wrong merchant.'
        onOk={() => switchMerchant(pendingMerchant ? pendingMerchant.value : '')}
      />

      <Modal open={bulkOpen} onClose={() => setBulkOpen(false)} title='Bulk upload overrides' icon={<UploadFileOutlinedIcon fontSize='small' />} width={640}>
        <BulkOverrideUpload
          merchant={merchant}
          merchantLabel={merchantLabel}
          onDone={() => {
            setBulkOpen(false)
            if (merchant) loadCurrent(merchant)
          }}
        />
      </Modal>
    </>
  )
}

/* ───────── "Add overrides" card: the editor rows (state lives in the page — Edit and the merchant switch change them too) ───────── */
function OverrideEditor({rows, setRows, merchant, saving, onSave}) {
  const filled = rows.filter((r) => String(r.f).trim() || String(r.t).trim()).length
  const setRow = (id, patch) => setRows((rs) => rs.map((r) => (r.id === id ? {...r, ...patch} : r)))
  const removeRow = (id) => setRows((rs) => (rs.length > 1 ? rs.filter((r) => r.id !== id) : rs))

  return (
    <ZoneCard
      title='Add overrides'
      description='One row per lane. Saving a lane that already has an override replaces it.'
      actions={filled ? <Badge status='info' label={`${filled} lane${filled === 1 ? '' : 's'}`} /> : null}
    >
      <Stack spacing={1.25}>
        {rows.map((r, idx) => (
          <Box key={r.id} sx={{p: 1.5, borderRadius: 2, border: 1, borderColor: 'divider'}}>
            {/* fixed grid: every cell = label row (18px) + 40px control, so labels and boxes line up */}
            <Box
              sx={{
                display: 'grid',
                columnGap: 1.5,
                rowGap: 1.25,
                alignItems: 'start',
                gridTemplateColumns: {
                  xs: 'repeat(2, minmax(0, 1fr))',
                  md: '28px minmax(120px, 1fr) 20px minmax(120px, 1fr) minmax(170px, 1.4fr) 92px 92px auto',
                },
              }}
            >
              <Box sx={{display: {xs: 'none', md: 'block'}}}>
                <SectionLabel sx={LABEL}>#</SectionLabel>
                <Typography sx={{fontFamily: MONO, fontSize: 13, color: 'text.secondary', lineHeight: `${CONTROL_H}px`}}>{idx + 1}</Typography>
              </Box>
              <Box sx={{minWidth: 0}}>
                <SectionLabel sx={LABEL}>From pincode</SectionLabel>
                <TextField fullWidth size='small' sx={FIELD_SX} value={r.f} inputProps={{...PIN_INPUT, 'aria-label': `Lane ${idx + 1} from pincode`}} placeholder='110077'
                  onChange={(e) => setRow(r.id, {f: digitsOnly(e.target.value)})} />
              </Box>
              <Box sx={{display: {xs: 'none', md: 'block'}}}>
                <SectionLabel sx={LABEL}>&nbsp;</SectionLabel>
                <Box sx={{height: CONTROL_H, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled'}}>
                  <ArrowForwardIcon sx={{fontSize: 18}} />
                </Box>
              </Box>
              <Box sx={{minWidth: 0}}>
                <SectionLabel sx={LABEL}>To pincode</SectionLabel>
                <TextField fullWidth size='small' sx={FIELD_SX} value={r.t} inputProps={{...PIN_INPUT, 'aria-label': `Lane ${idx + 1} to pincode`}} placeholder='400001'
                  onChange={(e) => setRow(r.id, {t: digitsOnly(e.target.value)})} />
              </Box>
              <Box sx={{minWidth: 0, gridColumn: {xs: '1 / -1', md: 'auto'}}}>
                <SectionLabel sx={LABEL}>Zone</SectionLabel>
                <SearchSelect
                  value={r.z}
                  placeholder='Keep base zone'
                  sx={SELECT_SX}
                  options={ZONE_OPTIONS}
                  onChange={(v) => setRow(r.id, {z: v || ''})}
                />
              </Box>
              <Box sx={{minWidth: 0}}>
                <SectionLabel sx={LABEL}>Min TAT</SectionLabel>
                <TextField fullWidth size='small' sx={FIELD_SX} value={r.mi} placeholder='days'
                  inputProps={{inputMode: 'decimal', 'aria-label': `Lane ${idx + 1} min TAT in days`}}
                  onChange={(e) => setRow(r.id, {mi: decimalOnly(e.target.value)})} />
              </Box>
              <Box sx={{minWidth: 0}}>
                <SectionLabel sx={LABEL}>Max TAT</SectionLabel>
                <TextField fullWidth size='small' sx={FIELD_SX} value={r.mx} placeholder='days'
                  inputProps={{inputMode: 'decimal', 'aria-label': `Lane ${idx + 1} max TAT in days`}}
                  onChange={(e) => setRow(r.id, {mx: decimalOnly(e.target.value)})} />
              </Box>
              <Box sx={{gridColumn: {xs: '1 / -1', md: 'auto'}}}>
                <SectionLabel sx={{...LABEL, display: {xs: 'none', md: 'block'}}}>&nbsp;</SectionLabel>
                <Stack direction='row' alignItems='center' sx={{height: CONTROL_H}}>
                  <Tooltip title={rows.length === 1 ? 'Keep at least one row' : 'Remove this row (nothing is saved)'}>
                    <span>
                      <IconButton size='small' onClick={() => removeRow(r.id)} disabled={rows.length === 1} aria-label='Remove row'>
                        <DeleteOutlineIcon sx={{fontSize: 18}} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </Box>
            </Box>
          </Box>
        ))}
      </Stack>

      <Stack direction='row' spacing={1.5} alignItems='center' flexWrap='wrap' useFlexGap sx={{mt: 2}}>
        <Button variant='outlined' size='small' startIcon={<AddIcon />} onClick={() => setRows((rs) => [...rs, BLANK_ROW()])}
          sx={{...BTN_SX, fontWeight: 600}}>
          Add lane
        </Button>
        <Button variant='contained' disableElevation size='small' startIcon={<SaveOutlinedIcon />} onClick={onSave} disabled={saving || !merchant}
          sx={BTN_SX}>
          {saving ? 'Saving…' : 'Save overrides'}
        </Button>
        {!merchant ? <Typography sx={{fontSize: 12, color: 'text.disabled'}}>Select a merchant first.</Typography> : null}
      </Stack>

      <Note sx={{mt: 2}}>
        Leave Zone empty to change only the TAT. New orders pick up changes straight away.
      </Note>

    </ZoneCard>
  )
}
OverrideEditor.propTypes = {
  rows: PropTypes.array.isRequired,
  setRows: PropTypes.func.isRequired,
  merchant: PropTypes.string,
  saving: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
}

/* ───────── "Current overrides": section title outside the card, then ONE card (the table's own) — no card-in-card ───────── */
function CurrentOverrides({merchant, merchantLabel, current, loading, error, columns, onReload, onBulkRemove}) {
  const summary = useMemo(() => {
    if (!current) return ''
    const up = current.rows.filter((r) => r.compare === 'up').length
    const down = current.rows.filter((r) => r.compare === 'down').length
    const parts = []
    if (up) parts.push(`${up.toLocaleString('en-IN')} dearer`)
    if (down) parts.push(`${down.toLocaleString('en-IN')} cheaper`)
    return parts.length ? ` ${parts.join(', ')} than the Prozo zone.` : ''
  }, [current])

  return (
    <Box sx={{maxWidth: PAGE_MAX, mt: 3, ...NO_P_MARGIN}}>
      <Stack direction='row' alignItems='flex-end' justifyContent='space-between' spacing={2} sx={{mb: 1.25, px: 0.5}}>
        <Box sx={{minWidth: 0}}>
          <Typography sx={{fontSize: 16, fontWeight: 700, color: 'text.primary'}}>
            Current overrides
            {current ? (
              <Box component='span' sx={{ml: 1, fontSize: 13, fontWeight: 600, color: 'text.secondary'}}>
                {current.rows.length.toLocaleString('en-IN')}{current.capped ? '+' : ''}
              </Box>
            ) : null}
          </Typography>
          {merchant ? (
            <Typography sx={{fontSize: 12.5, color: 'text.secondary', mt: 0.25}}>
              {merchantLabel || 'This merchant'}.{summary}
            </Typography>
          ) : null}
        </Box>
        {merchant ? (
          <Tooltip title='Reload'>
            <span>
              <IconButton size='small' onClick={onReload} disabled={loading} aria-label='Reload current overrides'>
                <RefreshIcon sx={{fontSize: 18}} />
              </IconButton>
            </span>
          </Tooltip>
        ) : null}
      </Stack>

      {!merchant || error || !current ? (
        <Box sx={{...CARD, px: 3, py: 2.5}}>
          {!merchant ? (
            <Typography sx={{fontSize: 13, color: 'text.secondary'}}>Select a merchant to see its overrides.</Typography>
          ) : error ? (
            <Note tone='warning'>Could not load the overrides: {error}</Note>
          ) : (
            <Typography sx={{fontSize: 13, color: 'text.secondary'}}>Loading overrides…</Typography>
          )}
        </Box>
      ) : (
        <Box sx={TABLE_SX}>
          {current.capped ? (
            <Note tone='warning' sx={{mb: 1.5}}>Showing the first {CURRENT_MAX_ROWS.toLocaleString('en-IN')} lanes only.</Note>
          ) : null}
          <SupplyTable
            columns={columns}
            rows={current.rows}
            rowKey={(r) => r.id}
            searchKeys={['f', 't', 'fromCity', 'toCity', 'zoneName', 'baseName']}
            searchPlaceholder='Search pincode, city or zone…'
            filters={CURRENT_FILTERS}
            defaultSort={{key: 'updated', dir: 'desc'}}
            pageSize={25}
            exportName={`zone-overrides-${merchant}`}
            emptyText='No overrides for this merchant.'
            bulkActions={[
              {label: 'Remove', icon: <DeleteOutlineIcon fontSize='small' />, danger: true, confirm: false, onClick: onBulkRemove},
            ]}
          />
        </Box>
      )}
    </Box>
  )
}
CurrentOverrides.propTypes = {
  merchant: PropTypes.string,
  merchantLabel: PropTypes.string,
  current: PropTypes.shape({rows: PropTypes.array, capped: PropTypes.bool}),
  loading: PropTypes.bool,
  error: PropTypes.string,
  columns: PropTypes.array.isRequired,
  onReload: PropTypes.func.isRequired,
  onBulkRemove: PropTypes.func.isRequired,
}

/* ───────── bulk xlsx upload → same delta endpoint ───────── */
function BulkOverrideUpload({merchant, merchantLabel, onDone}) {
  const toast = useToast()
  const [file, setFile] = useState(null)
  const [busy, setBusy] = useState(false)

  const upload = async () => {
    if (!merchant) return toast('Select a merchant first.', 'warning')
    if (!file) return toast('Drop the xlsx file first.', 'warning')
    setBusy(true)
    let data
    try {
      data = await file.arrayBuffer()
    } catch (err) {
      setBusy(false)
      return toast('Could not read the file.', 'error')
    }
    try {
      const wb = XLSX.read(data, {type: 'array', cellDates: true})
      const sheetName = wb.SheetNames.includes('Sheet1') ? 'Sheet1' : wb.SheetNames[0]
      const rows = XLSX.utils.sheet_to_row_object_array(wb.Sheets[sheetName], {header: 0, raw: false, dateNF: 'yyyy-mm-dd'})
      if (!rows.length) return toast('The sheet has no rows.', 'warning')
      const res = await post(`${apiConstants.MERCHANT_CONFIG}/${merchant}/zones`, rows)
      const msg = resultMessage(res?.data)
      toast(msg.text, msg.tone)
      onDone()
    } catch (err) {
      toast(apiMessage(err), 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Box sx={{p: 2.5}}>
      <Typography sx={{fontSize: 12.5, color: 'text.secondary', mb: 1.5}}>
        Uploading for <b>{merchantLabel || 'no merchant selected'}</b>. Columns: <b>f</b> and <b>t</b> (pickup and drop pincode),{' '}
        <b>z</b> (zone 1–5), <b>mi</b> and <b>mx</b> (TAT in days), <b>dl</b> (1 to delete the lane). Lanes not in the file stay as they are.
      </Typography>
      <FileDrop accept='.xlsx,.xls' title='Drop the xlsx file here' hint='The first sheet is read' maxMB={10} onFile={(f) => setFile(f)} onError={(m) => toast(m, 'error')} />
      <Stack direction='row' spacing={1.5} alignItems='center' sx={{mt: 2}}>
        <Button variant='contained' disableElevation size='small' onClick={upload} disabled={busy || !file || !merchant}
          sx={BTN_SX}>
          {busy ? 'Uploading…' : 'Upload overrides'}
        </Button>
        <DownloadSampleFile fileName='BULK_ZONE_OVERRIDE' excel='excel' />
      </Stack>
    </Box>
  )
}
BulkOverrideUpload.propTypes = {merchant: PropTypes.string, merchantLabel: PropTypes.string, onDone: PropTypes.func.isRequired}
